import type { ReactNode } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useDeployment } from "../deployment/DeploymentContext";
import { NavIcon } from "./NavIcon";
import { AppearanceMenu } from "./AppearanceMenu";

interface NavItem {
  to: string;
  label: string;
  icon: string;
  roles?: string[];
  /** Hidden for a platform-only admin (tenantId: null) - their account has no tenant data for
   *  these pages to operate on. See ProtectedRoute's matching requireTenant guard. */
  tenantOnly?: boolean;
  /** Starts a new labeled group in the sidebar (this item's own section label, not a separate
   *  divider item) - e.g. "WORKSPACE" above Dashboard, "PLATFORM" above Overview. */
  section?: string;
  /** Only exists on the hosted SaaS (billing, platform administration) - hidden on-prem. */
  saasOnly?: boolean;
  /** Only exists on an on-premise install (license management). */
  onPremOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/dashboard", label: "Dashboard", icon: "dashboard", tenantOnly: true, section: "Workspace" },
  { to: "/getting-started", label: "Getting started", icon: "checkCircle", tenantOnly: true },
  { to: "/folders", label: "Folders", icon: "folder", tenantOnly: true },
  { to: "/reports", label: "Reports", icon: "reports", tenantOnly: true },
  { to: "/schedules", label: "Schedules", icon: "clock", roles: ["OWNER", "ADMIN", "DESIGNER"], tenantOnly: true, section: "Automate" },
  { to: "/settings/email", label: "Email", icon: "mail", roles: ["OWNER", "ADMIN"], tenantOnly: true },
  { to: "/settings/branding", label: "Branding", icon: "palette", roles: ["OWNER", "ADMIN", "DESIGNER"], tenantOnly: true },
  { to: "/api-keys", label: "API Keys", icon: "key", roles: ["OWNER", "ADMIN"], tenantOnly: true, section: "Configure" },
  { to: "/users", label: "Users", icon: "users", roles: ["OWNER", "ADMIN"], tenantOnly: true },
  { to: "/billing", label: "Billing", icon: "card", tenantOnly: true, saasOnly: true },
  { to: "/settings/license", label: "License", icon: "shield", roles: ["OWNER", "ADMIN"], tenantOnly: true, onPremOnly: true },
  { to: "/integration-sample", label: "Integration Sample", icon: "link", tenantOnly: true },
  { to: "/admin/overview", label: "Overview", icon: "chart", roles: ["PLATFORM_SUPERADMIN"], section: "Platform", saasOnly: true },
  { to: "/admin/tenants", label: "Tenants", icon: "building", roles: ["PLATFORM_SUPERADMIN"], saasOnly: true },
  { to: "/admin/revenue", label: "Revenue", icon: "banknote", roles: ["PLATFORM_SUPERADMIN"], saasOnly: true },
  { to: "/admin/email", label: "Email defaults", icon: "mail", roles: ["PLATFORM_SUPERADMIN"], saasOnly: true },
  { to: "/admin/licenses", label: "Self-host licenses", icon: "shield", roles: ["PLATFORM_SUPERADMIN"], saasOnly: true },
];

export function Layout({ children }: { children: ReactNode }) {
  const { user, hasRole, logout } = useAuth();
  const { isOnPrem, status } = useDeployment();
  const navigate = useNavigate();

  async function handleLogout() {
    await logout();
    navigate("/login");
  }

  return (
    <div className="app-shell">
      <aside className="app-sidebar">
        <div className="app-brand">
          <div className="app-brand-mark">RF</div>
          <div>
            <span className="app-brand-name">ReportForge</span>
            <span className="app-brand-caption">Report Studio</span>
          </div>
        </div>
        <nav>
          {NAV_ITEMS.filter(
            (item) =>
              (!item.roles || hasRole(...(item.roles as any))) &&
              (!item.tenantOnly || user?.tenantId) &&
              (!item.saasOnly || !isOnPrem) &&
              (!item.onPremOnly || isOnPrem)
          ).map((item) => (
            <div key={item.to}>
              {item.section && <div className="nav-section-label">{item.section}</div>}
              <NavLink
                to={item.to}
                className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}
              >
                <NavIcon name={item.icon} />
                {item.label}
              </NavLink>
            </div>
          ))}
        </nav>
        <div className="app-sidebar-footer">
          <div className="app-sidebar-footer-row">
            <NavLink to="/account" className="app-user app-user-link" title="Your account & password">{user?.email}</NavLink>
            <AppearanceMenu />
          </div>
          <button type="button" className="btn-secondary" onClick={handleLogout}>
            Log out
          </button>
          {status?.version && <div className="app-version">ReportForge v{status.version}</div>}
        </div>
      </aside>
      <main className="app-content">
        {isOnPrem && status?.status === "EXPIRING" && (
          <div className="license-banner">
            Your license expires in <strong>{status.daysRemaining} day{status.daysRemaining === 1 ? "" : "s"}</strong>
            {status.expiresAt ? <> ({new Date(status.expiresAt).toLocaleDateString()})</> : null}. Renew it to avoid an
            interruption.{" "}
            {hasRole("OWNER", "ADMIN") && <Link to="/settings/license">Renew now →</Link>}
          </div>
        )}
        {children}
      </main>
    </div>
  );
}
